import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { Header, LoadingSpinner } from '@/components/Generic'
import { Illustration } from '@/components/Illustration/Illustration'
import { axiosInstance } from '@/methods/axiosConfig'
import { devLog } from '@/methods/devLog'
import { getUrlParam } from '@/methods/urlParams'
import { API_URL, JOINTSIGNING_SERVICE_PATH } from '@/constants/apiUrl'
import { PendingSignatoriesPage } from './PendingSignatoriesPage'

interface WelcomeData {
  blockingSignatories?: Array<{ firstName: string; lastName: string }>
}

/**
 * Pending page of the two-partner affordability checks without a signing step, picked in
 * scenarios.ts as `pending-signatories/<secondary flow configuration id>`. Joint-signing's
 * proceed-signatory sends the secondary partner here while the primary has not completed the
 * self-disclosure yet, with the proceed token in the URL; welcome-data names the partner it waits
 * on (`blockingSignatories`). When that name cannot be fetched, the generic pending page is shown.
 */
export const PendingDataCollectionPage = observer(() => {
  const store = useContext(StoreContext)
  const { pendingSignatoriesPage: trans } = store.TranslationsState.translations

  const proceedToken = getUrlParam('proceedToken')
  const proceedSource = getUrlParam('proceedSource')
  const [isLoading, setIsLoading] = useState(!!proceedToken)
  const [waitingOn, setWaitingOn] = useState<string | null>(null)

  useEffect(() => {
    if (!proceedToken) return

    const getWelcomeData = async () => {
      try {
        const query = proceedSource ? `?proceedSource=${proceedSource}` : ''
        const { data } = await axiosInstance.get<WelcomeData>(
          `${API_URL}/${JOINTSIGNING_SERVICE_PATH}/get-welcome-data/${proceedToken}${query}`,
          { withCredentials: false }
        )
        const [blocking] = data?.blockingSignatories ?? []
        if (blocking) setWaitingOn(`${blocking.firstName} ${blocking.lastName}`)
      } catch (error) {
        devLog('Error fetching welcome data:', error)
      }
      setIsLoading(false)
    }

    getWelcomeData()
  }, [])

  if (isLoading) {
    return (
      <div className="page-container p-50 mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  if (!waitingOn) return <PendingSignatoriesPage />

  return (
    <div className="page-container p-50 mobile-m-10">
      <Illustration type="pending-signatories" />

      <Header className="m-0-0-30">
        {trans.dataCollection.title.replaceAll('{name}', waitingOn)}
      </Header>

      <p className="generic__paragraph-centered m-0-0-30">
        {trans.dataCollection.description.replaceAll('{name}', waitingOn)}
      </p>
    </div>
  )
})
